import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Baby, ArrowLeft, Ruler, TrendingUp, Calendar, Trash2 } from 'lucide-react';
import { calculateAge } from '../mock';
import Logo from '../components/Logo';

const ChildDetail = () => {
  const navigate = useNavigate();
  const { childId } = useParams();
  const { children, getChildMeasurements, deleteChild } = useAuth();
  const child = children.find(c => c.id === childId);
  const measurements = getChildMeasurements(childId);

  if (!child) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Child not found</p>
      </div>
    );
  }

  // Handle both camelCase and snake_case from API
  const dateOfBirth = child.dateOfBirth || child.date_of_birth;
  const ageMonths = dateOfBirth ? calculateAge(dateOfBirth) : 0;
  const ageYears = Math.floor(ageMonths / 12);
  const remainingMonths = ageMonths % 12;

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete ${child.name}'s profile and all measurements?`)) {
      deleteChild(childId);
      navigate('/dashboard');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FFF5F0] via-[#E5F5FF] to-[#F0FFE5]">
      {/* Header */}
      <nav className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <Logo />
          <Button variant="outline" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Child Profile Card */}
        <Card className="p-8 mb-8 bg-white/90 backdrop-blur-sm">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-6">
              <Avatar className="w-24 h-24" style={{ backgroundColor: child.avatarColor }}>
                <AvatarFallback className="text-white text-4xl font-bold" style={{ backgroundColor: child.avatarColor }}>
                  {child.name[0]}
                </AvatarFallback>
              </Avatar>
              <div>
                <h1 className="text-3xl font-bold text-gray-800 mb-2">{child.name}</h1>
                <div className="space-y-1 text-gray-600">
                  <p className="flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Born: {dateOfBirth ? new Date(dateOfBirth).toLocaleDateString() : 'Not set'}
                  </p>
                  <p className="flex items-center gap-2">
                    <Baby className="w-4 h-4" />
                    Age: {ageMonths > 0 ? `${ageYears > 0 ? `${ageYears} year${ageYears > 1 ? 's' : ''} ` : ''}${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}` : 'Not available'}
                  </p>
                  <p className="flex items-center gap-2">
                    <Ruler className="w-4 h-4" />
                    {measurements.length} measurement{measurements.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => navigate(`/measure/${childId}`)}
                className="bg-gradient-to-r from-[#FFB4A2] via-[#F7DBA7] to-[#A8D5FF] hover:from-pink-600 hover:to-purple-600 text-white"
              >
                <Ruler className="w-4 h-4 mr-2" />
                New Measurement
              </Button>
              <Button variant="outline" onClick={handleDelete} className="text-red-600 hover:text-red-700">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </Card>

        {/* Growth Chart */}
        {measurements.length > 0 && (
          <Card className="p-6 mb-8 bg-white/90 backdrop-blur-sm">
            <div className="flex items-center gap-2 mb-6">
              <TrendingUp className="w-5 h-5 text-[#A8D5FF]" />
              <h2 className="text-xl font-bold text-gray-800">Growth Tracking</h2>
            </div>
            <div className="relative">
              <div className="flex items-end justify-between h-64 gap-2">
                {measurements.slice().reverse().map((measurement, index) => {
                  // Handle both camelCase and snake_case from API
                  const lengthCm = measurement.lengthCm || measurement.length_cm || 0;
                  const usSize = measurement.usSize || measurement.us_size;
                  const maxLength = Math.max(...measurements.map(m => m.lengthCm || m.length_cm || 0));
                  const height = maxLength > 0 ? (lengthCm / maxLength) * 100 : 0;
                  return (
                    <div key={measurement.id} className="flex-1 flex flex-col items-center gap-2">
                      <div className="text-center">
                        <p className="text-sm font-semibold text-gray-800">{lengthCm} cm</p>
                        <p className="text-xs text-gray-500">US {usSize}</p>
                      </div>
                      <div 
                        className="w-full bg-gradient-to-t from-[#FFB4A2] via-[#F7DBA7] to-[#A8D5FF] rounded-t-lg transition-all duration-500 hover:from-pink-600 hover:to-purple-600"
                        style={{ height: `${height}%`, minHeight: '40px' }}
                      ></div>
                      <p className="text-xs text-gray-600 mt-2">{new Date(measurement.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        )}

        {/* Measurements History */}
        <Card className="p-6 bg-white/90 backdrop-blur-sm">
          <h2 className="text-xl font-bold text-gray-800 mb-6">Measurement History</h2>
          
          {measurements.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 bg-gradient-to-br from-[#FFB4A2] to-[#F7DBA7] rounded-full flex items-center justify-center mx-auto mb-4">
                <Ruler className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">No Measurements Yet</h3>
              <p className="text-gray-600 mb-6">Start tracking {child.name}'s foot growth</p>
              <Button
                onClick={() => navigate(`/measure/${childId}`)}
                className="bg-gradient-to-r from-[#FFB4A2] via-[#F7DBA7] to-[#A8D5FF] hover:from-pink-600 hover:to-purple-600 text-white"
              >
                <Ruler className="w-4 h-4 mr-2" />
                Take First Measurement
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {measurements.map((measurement) => {
                // Handle both camelCase and snake_case from API
                const lengthCm = measurement.lengthCm || measurement.length_cm;
                const widthCm = measurement.widthCm || measurement.width_cm;
                const usSize = measurement.usSize || measurement.us_size;
                const euSize = measurement.euSize || measurement.eu_size;
                const ukSize = measurement.ukSize || measurement.uk_size;
                
                return (
                <Card key={measurement.id} className="p-4 bg-gradient-to-r from-gray-50 to-purple-50 border-2">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <p className="font-semibold text-gray-800">{new Date(measurement.date).toLocaleDateString()}</p>
                        <span className="px-2 py-1 bg-[#D9F2FF] text-[#6BA3D5] text-xs rounded-full">{measurement.method}</span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div>
                          <p className="text-xs text-gray-600">Length</p>
                          <p className="font-semibold text-gray-800">{lengthCm} cm</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Width</p>
                          <p className="font-semibold text-gray-800">{widthCm} cm</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">US Size</p>
                          <p className="font-semibold text-[#FFB4A2]">{usSize}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">EU Size</p>
                          <p className="font-semibold text-[#A8D5FF]">{euSize}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">UK Size</p>
                          <p className="font-semibold text-[#9ADFB4]">{ukSize}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ChildDetail;